import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Page, Layout, LegacyCard, ResourceList, ResourceItem, Text, TextField, Modal, Icon, FormLayout, EmptyState, Divider, InlineStack, InlineError, Badge, Box, Filters, ChoiceList, Button, Banner, Link, Thumbnail, MediaCard, Grid, Checkbox } from '@shopify/polaris';
import { ThemeIcon } from '@shopify/polaris-icons';

import { SkeletonPageCustom } from './SkeletonPageCustom';
import { DatePickerCustom } from './DatePickerCustom';
import { TimePickerCustom } from './TimePickerCustom';
import { Preview } from './Preview';
import { FetchCustom } from './FetchCustom';
import { useHostNavigate } from "../hooks";

export function Themes(props) {
    const navigate = useHostNavigate();
    const previewRef = useRef();

    const [loading, setLoading] = useState(true);
    const [model, setModel] = useState({ themes: [] });
    const [previewSettings, setPreviewSettings] = useState(null);
    const [previewThemeId, setPreviewThemeId] = useState(0);
    const [redirectUrl, setRedirectUrl] = useState('');
    const [modalThemeActive, setModalThemeActive] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [selectedTheme, setSelectedTheme] = useState({ action: '', id: 0, themeId: 0, name: '', headerText: '', startDate: '', startTime: '', endDate: '', endTime: '', launcherText: '', launcherShowText: false });
    const [error, setError] = useState({ headerText: false, startDate: false, startTime: false, endDate: false, endTime: false, wrongStartEndDates: false, dateRangeOverlaps: false });

    const fetch = FetchCustom();

    const cSharpDateToJsDate = function (val) {
        return new Date(Date.parse(val));
    }

    const dateRangeOverlaps = function (a_start, a_end, b_start, b_end) {
        if (a_start <= b_start && b_start <= a_end) return true; // b starts in a
        if (a_start <= b_end && b_end <= a_end) return true; // b ends in a
        if (b_start < a_start && a_end < b_end) return true; // a in b
        return false;
    }

    const populateData = async () => {
        props.setIsLoading(true);
        const data = await fetch('api/home/themes');

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setModel(data);
            setLoading(false);
            if (data.myThemes.filter(p => !p.main && p.status === 'Active').length) {
                getPreviewTheme(data.myThemes.filter(p => !p.main && p.status === 'Active')[0].themeId, false);
            }
            else {
                getPreviewTheme(data.myThemes.filter(p => p.main)[0].themeId, false);
            }
        }
    };

    const getUser = async () => {
        if (!props.userLoaded) {
            props.setUserLoaded(true);
            const data = await fetch('api/home/userInfo');

            if (data.redirectUrl) {
                setRedirectUrl(data.redirectUrl);
            }
            else if (JSON.stringify(props.user) !== JSON.stringify(data)) {
                props.setUser(data);
                localStorage.setItem('user', JSON.stringify(data));
            }
        }
    };

    const getPreviewTheme = async (themeId, fromClick) => {
        setPreviewThemeId(themeId);

        const data = await fetch('api/home/getPreviewTheme?themeId=' + themeId);

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setPreviewSettings(data);
            if (fromClick) {
                previewRef.current.open();
            }
        }
    };

    const saveChanges = async () => {
        var hasError = false;

        if (selectedTheme.action !== 'Remove') {
            if (!error.headerText && !selectedTheme.headerText) {
                handleError('headerText', true);
                hasError = true;
            }

            const startDate = cSharpDateToJsDate(selectedTheme.startDate + ' ' + selectedTheme.startTime);
            const endDate = cSharpDateToJsDate(selectedTheme.endDate + ' ' + selectedTheme.endTime);

            if (startDate > endDate) {
                handleError('wrongStartEndDates', true);
                hasError = true;
            }

            for (var i = 0; i < model.myThemes.length; i++) {
                const myTheme = model.myThemes[i];
                if (myTheme.main || myTheme.themeId === selectedTheme.themeId) {
                    continue;
                }

                var themeStartDate = cSharpDateToJsDate(myTheme.startDate + ' ' + myTheme.startTime);
                var themeEndDate = cSharpDateToJsDate(myTheme.endDate + ' ' + myTheme.endTime);

                const overlap = dateRangeOverlaps(startDate, endDate, themeStartDate, themeEndDate);
                if (overlap) {
                    handleError('dateRangeOverlaps', true);
                    hasError = true;
                    break;
                }
            }

            if (hasError || error.headerText || error.startDate || error.startTime || error.endDate || error.endTime || error.wrongStartEndDates || error.dateRangeOverlaps) {
                return;
            }
        }

        props.setIsLoading(true);
        setProcessing(true);
        const data = await fetch(`api/home/${selectedTheme.action !== 'Remove' ? 'SaveTheme' : 'RemoveTheme'}`, selectedTheme);
        props.setIsLoading(false);
        setProcessing(false);

        toggleModalTheme();

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model['myThemes'] = data;
                return model;
            });

            if (selectedTheme.action !== 'Remove') {
                props.changeToastActive(true);
            }
            else {
                props.changeToastActive(true, 'Theme removed');
            }
        }
    };

    const handleError = useCallback(
        (fieldName, hasError) => {
            setError((prevState) => {
                let error = Object.assign({}, prevState);
                error[fieldName] = hasError;
                return error;
            });
        },
        [],
    );

    const handleChange = useCallback(
        (fieldName, newValue) => {
            setSelectedTheme((prevState) => {
                let model = Object.assign({}, prevState);
                model[fieldName] = newValue;
                return model;
            });

            if (fieldName === 'startDate' || fieldName === 'startTime' || fieldName === 'endDate' || fieldName === 'endTime') {
                handleError('wrongStartEndDates', false);
                handleError('dateRangeOverlaps', false);
            }
        },
        // eslint-disable-next-line
        [],
    );

    const toggleModalTheme = useCallback(
        () => {
            setError({ headerText: false, startDate: false, startTime: false, endDate: false, endTime: false, wrongStartEndDates: false, dateRangeOverlaps: false });
            setModalThemeActive(!modalThemeActive);
        },
        [modalThemeActive],
    );

    useEffect(() => {
        getUser();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (JSON.stringify(props.user) !== '{}') {
            document.title = "Themes | Stack Discounts";
            populateData();
        }
        // eslint-disable-next-line
    }, [props.user]);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);


    const themeCards = [];
    const themeCardsLength = model && model.themes ? (model.themes.length % 3 === 0 ? model.themes.length : model.themes.length + (3 - model.themes.length % 3)) : 0;

    for (var i = 0; i < themeCardsLength; i++) {
        const item = i < model.themes.length ? model.themes[i] : null;
        const used = i < model.themes.length && model.myThemes.map(p => p.themeId).indexOf(item.id) > -1;
        themeCards.push(<div className="card-custom" key={i}>
            {
                i < model.themes.length ?
                    <Box borderColor="border" borderWidth="025" background="bg-surface" borderRadius="300">
                        <div className="theme-card">
                            <div className="theme-card-preview" onClick={() => {
                                props.setIsLoading(true);
                                getPreviewTheme(item.id, true);
                            }}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fillRule="evenodd" d="M13 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm-1.5 0a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" /><path fillRule="evenodd" d="M10 4c-2.476 0-4.348 1.23-5.577 2.532a9.266 9.266 0 0 0-1.4 1.922 5.98 5.98 0 0 0-.37.818c-.082.227-.153.488-.153.728s.071.501.152.728c.088.246.213.524.371.818.317.587.784 1.27 1.4 1.922 1.229 1.302 3.1 2.532 5.577 2.532 2.476 0 4.348-1.23 5.577-2.532a9.265 9.265 0 0 0 1.4-1.922 5.98 5.98 0 0 0 .37-.818c.082-.227.153-.488.153-.728s-.071-.501-.152-.728a5.984 5.984 0 0 0-.371-.818 9.269 9.269 0 0 0-1.4-1.922c-1.229-1.302-3.1-2.532-5.577-2.532Zm-5.999 6.002v-.004c.004-.02.017-.09.064-.223a4.5 4.5 0 0 1 .278-.608 7.768 7.768 0 0 1 1.17-1.605c1.042-1.104 2.545-2.062 4.487-2.062 1.942 0 3.445.958 4.486 2.062a7.77 7.77 0 0 1 1.17 1.605c.13.24.221.447.279.608.047.132.06.203.064.223v.004c-.004.02-.017.09-.064.223a4.503 4.503 0 0 1-.278.608 7.768 7.768 0 0 1-1.17 1.605c-1.042 1.104-2.545 2.062-4.487 2.062-1.942 0-3.445-.958-4.486-2.062a7.766 7.766 0 0 1-1.17-1.605 4.5 4.5 0 0 1-.279-.608c-.047-.132-.06-.203-.064-.223Z" /></svg>
                            </div>
                            <img alt={item.name} className="card-image" src={item.logoLargeImage} />
                            <Box paddingInline="400" paddingBlockStart="200" paddingBlockEnd="400">
                                <InlineStack gap="400" wrap={false} blockAlign="end">
                                    <div className="w-100">
                                        <Text variant="bodyMd" fontWeight="bold" as="h3">
                                            {item.name}
                                        </Text>
                                        <div>{item.headerText}</div>
                                    </div>
                                    <Button onClick={() => {
                                        setSelectedTheme({ action: 'Add', id: 0, themeId: item.id, name: item.name, headerText: item.headerText, startDate: item.startDate, startTime: item.startTime, endDate: item.endDate, endTime: item.endTime, launcherText: item.launcherText, launcherShowText: item.launcherShowText });
                                        toggleModalTheme();
                                    }} disabled={used}>{used ? 'Used' : 'Add'}</Button>
                                </InlineStack>
                            </Box>
                        </div>
                    </Box>
                    : null
            }
        </div>);
    }

    const mainBadge = model && model.myThemes ? (model.myThemes.filter(p => !p.main && p.status === 'Active').length ? "info" : "success") : "info";
    const mainStatus = model && model.myThemes ? (mainBadge === 'info' ? "Default" : "Active") : "Default";

    const modalTheme = (
        <Modal
            title={selectedTheme.action + ' ' + selectedTheme.name}
            open={modalThemeActive}
            onClose={() => toggleModalTheme()}
            primaryAction={{
                content: selectedTheme.action === 'Edit' ? 'Save' : selectedTheme.action,
                onAction: () => {
                    if (props.user.viewOnly) return;
                    saveChanges();
                },
                loading: processing
            }}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: () => toggleModalTheme()
                },
            ]}
        >
            <div id="modalTheme">
                <Modal.Section>
                    {
                        selectedTheme.action !== 'Remove' ?
                            <FormLayout>
                                <TextField
                                    value={selectedTheme.headerText}
                                    label="Header text"
                                    helpText="This text will also be used as a combined title for all active automatic campaigns."
                                    onChange={(newValue) => handleChange('headerText', newValue)}
                                    error={error.headerText ? 'Header text is required' : ''}
                                    onBlur={() => handleError('headerText', !selectedTheme.headerText)}
                                />
                                <Divider />
                                <FormLayout.Group>
                                    <Checkbox
                                        label="Show text on launcher"
                                        checked={selectedTheme.launcherShowText}
                                        onChange={(newValue) => handleChange('launcherShowText', newValue)}
                                    />
                                    <TextField
                                        value={selectedTheme.launcherText}
                                        onChange={(newValue) => handleChange('launcherText', newValue)}
                                        label="Launcher text"
                                        disabled={!selectedTheme.launcherShowText}
                                    />
                                </FormLayout.Group>
                                <Divider />
                                <Text fontWeight="semibold">Active dates</Text>
                                <FormLayout.Group>
                                    <DatePickerCustom
                                        label="Start date"
                                        fieldName="startDate"
                                        value={selectedTheme.startDate}
                                        handleChange={handleChange}
                                        handleError={handleError}
                                    />
                                    <TimePickerCustom
                                        label={'Start time (' + model.timezoneAbbreviation + ')'}
                                        fieldName="startTime"
                                        value={selectedTheme.startTime}
                                        handleChange={handleChange}
                                        handleError={handleError}
                                    />
                                </FormLayout.Group>
                                <FormLayout.Group>
                                    <DatePickerCustom
                                        label="End date"
                                        fieldName="endDate"
                                        value={selectedTheme.endDate}
                                        startDate={selectedTheme.startDate}
                                        startTime={selectedTheme.startTime}
                                        endTime={selectedTheme.endTime}
                                        handleChange={handleChange}
                                        handleError={handleError}
                                    />
                                    <TimePickerCustom
                                        label={'End time (' + model.timezoneAbbreviation + ')'}
                                        fieldName="endTime"
                                        value={selectedTheme.endTime}
                                        handleChange={handleChange}
                                        handleError={handleError}
                                    />
                                </FormLayout.Group>
                                {
                                    error.wrongStartEndDates ?
                                        <InlineError message="Start date should be before end date" />
                                        : null
                                }
                                {
                                    error.dateRangeOverlaps ?
                                        <InlineError message="Date range overlaps with existing themes" />
                                        : null
                                }
                            </FormLayout>
                            :
                            <div>Are you sure you want to remove this theme?</div>
                    }
                </Modal.Section>
            </div>
        </Modal>
    );

    return (
        loading ?
            <SkeletonPageCustom />
            :
            <Page title="Themes">
                {modalTheme}
                <Layout>
                    <Layout.Section>
                        <LegacyCard>
                            <div id="themes">
                                <ResourceList
                                    resourceName={{ singular: 'theme', plural: 'themes' }}
                                    items={model.myThemes}
                                    renderItem={(item) => {
                                        const { id, name } = item;
                                        const media = <Thumbnail size="large" alt={item.name} source={item.logoSmallImage} />;

                                        return (
                                            <ResourceItem
                                                verticalAlignment="center"
                                                id={id}
                                                name={name}
                                                media={media}
                                                accessibilityLabel={`View details for ${name}`}
                                                shortcutActions={item.main ?
                                                    [
                                                        {
                                                            content: 'Customize',
                                                            onAction: () => navigate('/themes/main')
                                                        }
                                                    ]
                                                    :
                                                    [
                                                        {
                                                            content: 'Edit',
                                                            onAction: () => {
                                                                setSelectedTheme({ action: 'Edit', id: id, themeId: item.themeId, name: item.name, headerText: item.headerText, startDate: item.startDate, startTime: item.startTime, endDate: item.endDate, endTime: item.endTime, launcherText: item.launcherText, launcherShowText: item.launcherShowText });
                                                                toggleModalTheme();
                                                            }
                                                        },
                                                        {
                                                            content: 'Remove',
                                                            onAction: () => {
                                                                setSelectedTheme({ action: 'Remove', id: id, themeId: item.themeId, name: item.name });
                                                                toggleModalTheme();
                                                            }
                                                        }
                                                    ]}
                                                persistActions
                                            >
                                                <InlineStack gap={{ xs: 600, md: 800, lg: 1200 }} wrap={false} blockAlign="start">
                                                    <div className="theme-resource-preview" onClick={() => {
                                                        props.setIsLoading(true);
                                                        getPreviewTheme(item.themeId, true);
                                                    }}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fillRule="evenodd" d="M13 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm-1.5 0a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" /><path fillRule="evenodd" d="M10 4c-2.476 0-4.348 1.23-5.577 2.532a9.266 9.266 0 0 0-1.4 1.922 5.98 5.98 0 0 0-.37.818c-.082.227-.153.488-.153.728s.071.501.152.728c.088.246.213.524.371.818.317.587.784 1.27 1.4 1.922 1.229 1.302 3.1 2.532 5.577 2.532 2.476 0 4.348-1.23 5.577-2.532a9.265 9.265 0 0 0 1.4-1.922 5.98 5.98 0 0 0 .37-.818c.082-.227.153-.488.153-.728s-.071-.501-.152-.728a5.984 5.984 0 0 0-.371-.818 9.269 9.269 0 0 0-1.4-1.922c-1.229-1.302-3.1-2.532-5.577-2.532Zm-5.999 6.002v-.004c.004-.02.017-.09.064-.223a4.5 4.5 0 0 1 .278-.608 7.768 7.768 0 0 1 1.17-1.605c1.042-1.104 2.545-2.062 4.487-2.062 1.942 0 3.445.958 4.486 2.062a7.77 7.77 0 0 1 1.17 1.605c.13.24.221.447.279.608.047.132.06.203.064.223v.004c-.004.02-.017.09-.064.223a4.503 4.503 0 0 1-.278.608 7.768 7.768 0 0 1-1.17 1.605c-1.042 1.104-2.545 2.062-4.487 2.062-1.942 0-3.445-.958-4.486-2.062a7.766 7.766 0 0 1-1.17-1.605 4.5 4.5 0 0 1-.279-.608c-.047-.132-.06-.203-.064-.223Z" /></svg>
                                                    </div>
                                                    <div className="mw-130">
                                                        <Text variant="bodyMd" fontWeight="bold" as="h3">
                                                            {name}
                                                        </Text>
                                                        <div>{item.headerText}</div>
                                                        <div className="mt-4">
                                                            {
                                                                item.main ?
                                                                    <Badge tone={mainBadge}>{mainStatus}</Badge>
                                                                    :
                                                                    <Badge tone={item.status === "Active" ? "success" : (item.status === "Scheduled" ? "attention" : "default")}>{item.status}</Badge>
                                                            }
                                                        </div>
                                                    </div>
                                                    {
                                                        !item.main ?
                                                            <div>
                                                                <Text variant="bodyMd" fontWeight="bold" as="h3">
                                                                    Active dates
                                                                </Text>
                                                                <div>{item.dateRange}</div>
                                                            </div>
                                                            : null
                                                    }
                                                </InlineStack>
                                            </ResourceItem>
                                        );
                                    }}
                                />
                            </div>
                        </LegacyCard>
                    </Layout.Section>
                    <Layout.Section>
                        <LegacyCard>
                            <LegacyCard.Section>
                                <div className="card-custom-header">
                                    <InlineStack gap="400" wrap={false}>
                                        <div className="card-header-icon">
                                            <Icon source={ThemeIcon} tone="info" />
                                        </div>
                                        <div>
                                            <div><Text variant="headingMd" as="h6">Special themes</Text></div>
                                            <div>Those themes will overwrite the main theme for a specific period.</div>
                                        </div>
                                    </InlineStack>
                                </div>
                                <div className="container-grid">
                                    {themeCards.length ? themeCards : null}
                                </div>
                            </LegacyCard.Section>
                        </LegacyCard>
                    </Layout.Section>
                </Layout>
                {
                    previewSettings ?
                        <Preview ref={previewRef} settings={previewSettings} user={props.user} />
                        : null
                }

            </Page>
    );
}